<script setup>
/**
 * How OKANE works, one idea per step: what a transaction is, how money gets in
 * and out of the app, categories, tags, filing a whole import at once — and a
 * last step that sends you to Settings, because currency and bank are the first
 * things worth setting.
 *
 * The language picker sits in the header rather than in a step: someone who
 * cannot read the first step needs it before they need anything else. It is
 * the app's own setting, so the whole app switches with it.
 *
 * Opened once by itself on a first visit (`stores/help.js`) and from the
 * header's ? after that.
 */
import { computed, onMounted, onUnmounted, ref } from 'vue'
import Sheet from '../ui/Sheet.vue'
import Icon from '../ui/Icon.vue'
import Kbd from '../ui/Kbd.vue'
import { ariaCombo } from '../../lib/shortcuts'
import { closeSheet, openSheet } from '../../stores/state'
import { LANGUAGES, language, setLanguage, t } from '../../stores/i18n'
import { HELP_STEPS as STEPS } from '../../stores/help'

const at = ref(0)
const step = computed(() => STEPS[at.value])
const last = computed(() => at.value === STEPS.length - 1)
const points = computed(() =>
  Array.from({ length: step.value.points }, (_, i) => t(`help.${step.value.key}.p${i + 1}`)),
)

const go = (index) => (at.value = Math.min(Math.max(index, 0), STEPS.length - 1))
const next = () => go(at.value + 1)
const back = () => go(at.value - 1)

/** The last step's way out: straight into the settings it has just described. */
const openSettings = () => openSheet('settings')

// The arrow keys turn the pages, unless they are busy in the language picker.
const onKey = (event) => {
  if (event.target?.tagName === 'SELECT') return
  if (event.key === 'ArrowRight') next()
  else if (event.key === 'ArrowLeft') back()
}
onMounted(() => document.addEventListener('keydown', onKey))
onUnmounted(() => document.removeEventListener('keydown', onKey))
</script>

<template>
  <Sheet
    :title="t('help.title')"
    :subtitle="t('help.step', { n: at + 1, of: STEPS.length })"
    @close="closeSheet"
  >
    <template #actions>
      <label class="-mt-0.5 shrink-0">
        <span class="sr-only">{{ t('settings.language') }}</span>
        <select
          id="help-language"
          class="input !w-auto !py-1.5 !pr-8 !pl-2.5 !text-[13px]"
          :value="language"
          @change="setLanguage($event.target.value)"
        >
          <option v-for="l in LANGUAGES" :key="l.code" :value="l.code">{{ l.label }}</option>
        </select>
      </label>
    </template>

    <!-- Keyed by step, so a new page is announced and starts at its top. -->
    <section :key="step.key" class="grid gap-4" :data-step="step.key" aria-live="polite">
      <div class="flex items-center gap-3">
        <span class="grid size-11 shrink-0 place-items-center rounded-2xl bg-accent-soft">
          <Icon :name="step.icon" :size="22" class="text-accent" />
        </span>
        <h3 class="text-[17px] leading-tight font-semibold">{{ t(`help.${step.key}.title`) }}</h3>
      </div>

      <p class="text-[14px] leading-relaxed">{{ t(`help.${step.key}.lead`) }}</p>

      <ul class="grid gap-2.5">
        <li v-for="(point, i) in points" :key="i" class="flex gap-2.5 text-[13.5px] leading-relaxed text-muted">
          <span class="mt-[0.55em] size-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" />
          <span>{{ point }}</span>
        </li>
      </ul>

      <div v-if="step.tokens" class="flex flex-wrap gap-1.5">
        <code
          v-for="token in step.tokens"
          :key="token"
          class="rounded-md border border-line bg-raised px-1.5 py-0.5 text-[12.5px]"
        >{{ token }}</code>
      </div>
    </section>

    <template #footer>
      <!-- Where you are, and a way to jump: one dot per step. Not on a phone,
           where the header already says which step this is and the row needs
           the room for its buttons. -->
      <div class="flex-1 sm:hidden" />
      <nav class="hidden flex-1 items-center gap-1.5 sm:flex" :aria-label="t('help.steps')">
        <button
          v-for="(s, i) in STEPS"
          :key="s.key"
          class="h-2 rounded-full transition-all"
          :class="i === at ? 'w-5 bg-accent' : 'w-2 bg-line hover:bg-muted'"
          :aria-label="t('help.goTo', { n: i + 1, title: t(`help.${s.key}.title`) })"
          :aria-current="i === at ? 'step' : undefined"
          @click="go(i)"
        />
      </nav>
      <button v-if="at > 0" class="btn-soft whitespace-nowrap" @click="back">{{ t('help.back') }}</button>
      <!-- The last word is the way on, not a way out: the ✕ already closes. -->
      <button data-primary :aria-keyshortcuts="ariaCombo('mod+enter')" v-if="last" class="btn-primary whitespace-nowrap" @click="openSettings">
        <Icon name="settings" :size="16" /> {{ t('help.openSettings') }}
        <Kbd combo="mod+enter" />
      </button>
      <button data-primary :aria-keyshortcuts="ariaCombo('mod+enter')" v-else class="btn-primary whitespace-nowrap" @click="next">{{ t('help.next') }}<Kbd combo="mod+enter" /></button>
    </template>
  </Sheet>
</template>
